import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { DxDataGridComponent } from 'devextreme-angular';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { Observable, forkJoin } from 'rxjs';

import { AppDocumentos } from '../../core/models/appDocumentos.model';
import { AppDocumentosService } from '../../core/services/appDocumentos.service';
import { AppEmpresas } from '../../core/models/appEmpresas.model';
import { AppEmpresasService } from '../../core/services/appEmpresasService.service';
import { AppUsuario } from '../../core/models/appUsuario.model';
import { AppUsuarioService } from '../../core/services/appUsuarioService.service';
import { AppUsersDocs } from '../../core/models/appUsersDocs.model';
import { AppUsersDocsService } from '../../core/services/appUsersDocs.service';
import { AppA3DocService } from '../../core/services/appA3Doc.service';
import { AppA3DocDocument } from '../../core/models/appA3DocDocument.model';
import { MessageDialog } from '../../components/message-dialog/messageDialog';
import { SessionHelper } from '../../core/helper/sessionHelper';

@Component({
  selector: 'app-onboarding-detail',
  templateUrl: './onboarding-detail.component.html',
  styleUrls: ['./onboarding-detail.component.scss'],
  providers: [AppDocumentosService, AppEmpresasService, AppUsuarioService, AppUsersDocsService, AppA3DocService]
})
export class OnboardingDetailComponent implements OnInit, AfterViewInit {

  @ViewChild('documentGrid', { static: false }) documentGrid: DxDataGridComponent;
  @ViewChild('userGrid', { static: false }) userGrid: DxDataGridComponent;

  panelIcon = 'door-open'; // font awesome
  panelTitle = 'Onboarding';
  panelDescription = 'Onboarding';
  dataSourceDocs: AppA3DocDocument[];
  dataSourceDocsOriginal: AppA3DocDocument[];
  dataSourceUsuario: AppUsuario[];
  appEmpresasModel: AppEmpresas;
  appUserDocsModel: AppUsersDocs;
  categories: any[];
  company: string;
  companyId: string;
  loading: boolean;

  selectedDocuments: AppA3DocDocument[] = [];
  selectedUsers: AppUsuario[] = [];

  constructor(
    private appA3DocService: AppA3DocService,
    private appUsuarioService: AppUsuarioService,
    private appUserDocsService: AppUsersDocsService,
    private _messageDialog: MessageDialog,
    protected _sessionHelper: SessionHelper,
  ) { }

  ngOnInit(): void {
    this.clearArrays();
  }

  ngAfterViewInit(): void {
    this.checkSelections();
  }

  onEditorPreparing(e) {
    if (e.parentType === "searchPanel") {
      e.editorOptions.stylingMode = "underlined";
    }
  }

  calculateWorkerName(rowData) {
    return rowData.name + ' ' + rowData.surnames;
  }

  clearArrays(): void {
    this.dataSourceDocs = [];
    this.dataSourceDocsOriginal = [];
    this.dataSourceUsuario = [];
  }

  companySelectedChanged(data: any) {
    this.company = data[0].nif;
    this.companyId = data[0].id;
    this.getAllDocs();
    this.getUsers();
    this.checkSelections();
  }

  setCategories(categories: any[]) {
    if (categories.length > 0) {
      this.categories = categories;
    } else if (categories.length == 0) {
      this.categories = [];
    }
    this.checkSelections();
  }

  private getAllDocs(clear: boolean = true): void {
    clear && this.clearArrays();

    if (this.documentGrid?.instance) {
      this.documentGrid.instance.beginCustomLoading('');
      this.appA3DocService.getDocumentsWithoutPayrollForHrUser(0, this.company, false).then(
        response => {
          this.dataSourceDocsOriginal = response;
          this.documentGrid.instance.endCustomLoading();
        },
        error => {
          console.error('Error obteniendo documentos:', error);
          this.documentGrid.instance.endCustomLoading();
        }
      );
    } else {
      console.warn('documentGrid no está inicializado');
    }
  }

  private getUsers(): void {
    if (this.companyId) {
      this.userGrid.instance.beginCustomLoading('');
      this.appUsuarioService.getAllForSelectByCompany(this.companyId, true).subscribe(
        response => {
          this.dataSourceUsuario = response.map(user => ({
            ...user,
            fullName: `${user.name} ${user.surnames}`
          }));
          this.userGrid.instance.endCustomLoading();
        },
        error => {
          console.error('Error al obtener los usuarios', error);
        }
      );
    } else {
      this.dataSourceUsuario = [];
    }
  }

  private awaitDataSourceDocsOriginal(): Promise<void> {
    return new Promise(resolve => {
      const checkInterval = setInterval(() => {
        if (this.dataSourceDocsOriginal?.length > 0) {
          clearInterval(checkInterval);
          resolve();
        }
      }, 100);
    });
  }

  private async checkSelections() {
    await this.awaitDataSourceDocsOriginal();

    if (this.company && this.categories?.length > 0) {
      if (this.documentGrid && this.userGrid) {
        const selectedCategoryIds = this.categories.map(cat => cat.id);
        this.dataSourceDocs = this.dataSourceDocsOriginal.filter(doc =>
          doc.containersId.some(containerId => selectedCategoryIds.includes(containerId))
        );
      }
    } else {
      this.dataSourceDocs = [];
    }
  }


  onSaveClick(): void {
    this.loading = true;
    this.selectedDocuments = this.documentGrid.instance.getSelectedRowsData();
    this.selectedUsers = this.userGrid.instance.getSelectedRowsData();

    if (this.selectedDocuments.length > 0 && this.selectedUsers.length > 0) {

      this.documentGrid.instance.beginCustomLoading('');
      this.userGrid.instance.beginCustomLoading('');

      const updatePromises = this.selectedDocuments.map(document =>
        this.appA3DocService.getDoc(document.id)
          .then(result => {
            document.docData = result.content;
            return this.appA3DocService.updateDocUsers(document, this.selectedUsers, false, false)
              .then(response => {
                if (response.response.errorCode === 0) {
                  return { failed: false, documentName: document.title };
                } else {
                  return { failed: true, documentName: document.title };
                }
              });
          })
          .catch(error => {
            console.error('Error al actualizar el documento', document, error);
            return { failed: true, documentName: document.title };
          })
      );

      Promise.all(updatePromises)
        .then(results => {
          const failedDocuments = results.filter(result => result.failed).map(result => result.documentName);

          if (failedDocuments.length > 0) {
            this._messageDialog.ShowAlert(
              `Los siguientes documentos no pudieron ser asignados a los empleados: ${failedDocuments.join(', ')}`,
              'Error al asignar documentos'
            );
          } else {
            this.documentGrid.instance.endCustomLoading();
            this.userGrid.instance.endCustomLoading();

            this._messageDialog.ShowContinueMessage(
              'Todos los documentos fueron asignados a los empleados correctamente',
              'Asignación exitosa'
            ).then(() => {
              this.documentGrid.instance.clearSelection();
              this.userGrid.instance.clearSelection();
              this.dataSourceDocsOriginal = [];
              this.dataSourceDocs = [];
              this.selectedDocuments = [];
              this.selectedUsers = [];
              this.getAllDocs(false);
              this.checkSelections();
            });


          }
        })
        .finally(() => {
          this.loading = false;
        });

    } else {
      this._messageDialog.ShowAlert(
        'Es necesario seleccionar al menos un documento y un usuario',
        'Error al vincular los documentos'
      );
      this.loading = false;
    }
  }

}  
